import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Badge, Button, Icon, Modal } from 'antd'
import ReferenceSearch from './ReferenceSearch'
import { COLORS } from './utils'

const GRID_COLUMNS = 3
const GRID_GAP = 8
const GRID_PADDING = 12
const HEADER_HEIGHT = 30
const GROUP_GAP = 10
const DEFAULT_VIEWPORT_WIDTH = 760
const DEFAULT_VIEWPORT_HEIGHT = 440
const OVERSCAN_ROWS = 1
const SCROLL_IDLE_MS = 140
const SEARCH_DEBOUNCE_MS = 120

const GROUP_ORDER = [
  ['uploaded', 'Uploaded'],
  ['shots', 'Shots'],
  ['locations', 'Locations'],
  ['props', 'Props'],
  ['characters', 'Characters']
]

const getGroupKey = image => {
  if (image.sceneTitle === 'Uploaded') return 'uploaded'
  if (image.sceneTitle === 'Location') return 'locations'
  if (image.sceneTitle === 'Prop') return 'props'
  if (image.sceneTitle === 'Character') return 'characters'
  return 'shots'
}

const getGroupIcon = key => {
  if (key === 'locations') return 'environment'
  if (key === 'characters') return 'user'
  if (key === 'props') return 'appstore'
  if (key === 'uploaded') return 'cloud-upload'
  return 'picture'
}

const findFirstVisibleRowIndex = (rows, visibleStart) => {
  let low = 0
  let high = rows.length

  while (low < high) {
    const mid = Math.floor((low + high) / 2)
    const row = rows[mid]

    if (row.top + row.height < visibleStart) {
      low = mid + 1
    } else {
      high = mid
    }
  }

  return low
}

const ReferenceImage = memo(
  ({ image, selected, deferImage, onSelect }) => {
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
      setLoaded(false)
    }, [image.url])

    return (
      <div
        className="reference-image-item"
        style={{
          position: 'relative',
          borderRadius: 6,
          overflow: 'hidden',
          border: `2px solid ${selected ? COLORS.primary : 'transparent'}`,
          background: COLORS.background
        }}
      >
        <button
          type="button"
          onClick={() => onSelect(image.url)}
          style={{
            display: 'block',
            width: '100%',
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: 'pointer'
          }}
        >
          <div
            className={`reference-image-thumbnail${
              loaded && !deferImage ? ' reference-image-thumbnail--loaded' : ''
            }`}
            style={{
              width: '100%',
              aspectRatio: '1/1',
              background: COLORS.background
            }}
          >
            {!deferImage ? (
              <img
                src={image.url}
                alt={image.shotTitle || 'Reference'}
                loading="lazy"
                decoding="async"
                onLoad={() => setLoaded(true)}
                onError={() => setLoaded(true)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  opacity: loaded ? 1 : 0,
                  transition: 'opacity 0.12s ease-out'
                }}
              />
            ) : null}
          </div>
        </button>

        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '18px 6px 5px',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.68))',
            color: '#fff',
            fontSize: 10,
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            pointerEvents: 'none'
          }}
        >
          {image.shotTitle || 'Image'}
        </div>

        {selected ? (
          <div
            style={{
              position: 'absolute',
              top: 5,
              right: 5,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: COLORS.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
              pointerEvents: 'none'
            }}
          >
            <Icon type="check" style={{ color: '#fff', fontSize: 11 }} />
          </div>
        ) : null}

      </div>
    )
  }
)

const ReferencePanel = memo(
  ({
    visible,
    references,
    currentImage,
    selectedReferences,
    onApply,
    onCancel
  }) => {
    const viewportRef = useRef(null)
    const scrollFrameRef = useRef(null)
    const scrollIdleTimerRef = useRef(null)
    const isScrollingRef = useRef(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
    const [draftSelectedReferences, setDraftSelectedReferences] = useState([])
    const [viewportWidth, setViewportWidth] = useState(0)
    const [viewportHeight, setViewportHeight] = useState(0)
    const [scrollTop, setScrollTop] = useState(0)
    const [isScrolling, setIsScrolling] = useState(false)

    useEffect(() => {
      if (!visible) return

      setDraftSelectedReferences(
        Array.isArray(selectedReferences) ? selectedReferences : []
      )
      setSearchTerm('')
      setDebouncedSearchTerm('')
      setScrollTop(0)
    }, [selectedReferences, visible])

    useEffect(() => {
      if (!visible) return undefined

      const timerId = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm)
      }, SEARCH_DEBOUNCE_MS)

      return () => clearTimeout(timerId)
    }, [searchTerm, visible])

    useEffect(() => {
      if (!visible) {
        setScrollTop(0)
        setIsScrolling(false)
        isScrollingRef.current = false
        if (scrollIdleTimerRef.current) {
          clearTimeout(scrollIdleTimerRef.current)
          scrollIdleTimerRef.current = null
        }
        return undefined
      }

      isScrollingRef.current = true
      setIsScrolling(true)
      if (scrollIdleTimerRef.current) {
        clearTimeout(scrollIdleTimerRef.current)
      }
      scrollIdleTimerRef.current = setTimeout(() => {
        isScrollingRef.current = false
        setIsScrolling(false)
      }, SCROLL_IDLE_MS)

      const viewport = viewportRef.current
      if (!viewport) return undefined

      const updateSize = () => {
        setViewportWidth(width =>
          width === viewport.clientWidth ? width : viewport.clientWidth
        )
        setViewportHeight(height =>
          height === viewport.clientHeight ? height : viewport.clientHeight
        )
      }

      updateSize()

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(updateSize)
        resizeObserver.observe(viewport)
        return () => resizeObserver.disconnect()
      }

      window.addEventListener('resize', updateSize)
      return () => window.removeEventListener('resize', updateSize)
    }, [visible])

    useEffect(
      () => () => {
        if (scrollFrameRef.current) {
          cancelAnimationFrame(scrollFrameRef.current)
        }
        if (scrollIdleTimerRef.current) {
          clearTimeout(scrollIdleTimerRef.current)
        }
      },
      []
    )

    useEffect(() => {
      setScrollTop(0)
      if (viewportRef.current) viewportRef.current.scrollTop = 0
    }, [debouncedSearchTerm, visible])

    const selectedSet = useMemo(
      () => new Set(draftSelectedReferences || []),
      [draftSelectedReferences]
    )

    const handleSelect = useCallback(url => {
      setDraftSelectedReferences(previousReferences =>
        previousReferences.includes(url)
          ? previousReferences.filter(referenceUrl => referenceUrl !== url)
          : [...previousReferences, url]
      )
    }, [])

    const handleApply = useCallback(() => {
      onApply(draftSelectedReferences)
    }, [draftSelectedReferences, onApply])

    const handleCancel = useCallback(() => {
      onCancel()
    }, [onCancel])

    const handleClear = useCallback(() => {
      setDraftSelectedReferences([])
    }, [])

    const handleScroll = useCallback(event => {
      const nextScrollTop = event.currentTarget.scrollTop

      if (!isScrollingRef.current) {
        isScrollingRef.current = true
        setIsScrolling(true)
      }

      if (scrollIdleTimerRef.current) {
        clearTimeout(scrollIdleTimerRef.current)
      }

      scrollIdleTimerRef.current = setTimeout(() => {
        isScrollingRef.current = false
        setIsScrolling(false)
      }, SCROLL_IDLE_MS)

      if (scrollFrameRef.current) {
        cancelAnimationFrame(scrollFrameRef.current)
      }

      scrollFrameRef.current = requestAnimationFrame(() => {
        setScrollTop(nextScrollTop)
        scrollFrameRef.current = null
      })
    }, [])

    const groups = useMemo(() => {
      if (!visible) return []

      const normalizedSearchTerm = debouncedSearchTerm.trim().toLowerCase()
      const seen = new Set()
      const nextGroups = {
        uploaded: [],
        shots: [],
        locations: [],
        props: [],
        characters: []
      }

      ;(references || []).forEach(image => {
        if (!image || !image.url || image.url === currentImage) return
        if (seen.has(image.url)) return

        const label = `${image.shotTitle || ''} ${image.sceneTitle || ''}`
          .toLowerCase()
          .trim()

        if (normalizedSearchTerm && !label.includes(normalizedSearchTerm)) {
          return
        }

        seen.add(image.url)
        nextGroups[getGroupKey(image)].push(image)
      })

      return GROUP_ORDER.map(([key, title]) => ({
        key,
        title,
        images: nextGroups[key]
      })).filter(group => group.images.length > 0)
    }, [currentImage, debouncedSearchTerm, references, visible])

    const totalCount = groups.reduce(
      (count, group) => count + group.images.length,
      0
    )

    const effectiveWidth = viewportWidth || DEFAULT_VIEWPORT_WIDTH
    const cellSize = Math.max(
      1,
      (effectiveWidth - GRID_PADDING * 2 - GRID_GAP * (GRID_COLUMNS - 1)) /
        GRID_COLUMNS
    )
    const rowPitch = cellSize + GRID_GAP

    const { contentHeight, rows } = useMemo(() => {
      if (!visible || groups.length === 0) {
        return { contentHeight: 0, rows: [] }
      }

      const nextRows = []
      let nextTop = GRID_PADDING

      groups.forEach(group => {
        nextRows.push({
          type: 'header',
          key: `${group.key}-header`,
          top: nextTop,
          height: HEADER_HEIGHT,
          group
        })
        nextTop += HEADER_HEIGHT

        const rowCount = Math.ceil(group.images.length / GRID_COLUMNS)

        for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
          const startIndex = rowIndex * GRID_COLUMNS

          nextRows.push({
            type: 'images',
            key: `${group.key}-row-${rowIndex}`,
            top: nextTop,
            height: cellSize,
            images: group.images.slice(startIndex, startIndex + GRID_COLUMNS)
          })
          nextTop += rowPitch
        }

        if (rowCount > 0) nextTop -= GRID_GAP
        nextTop += GROUP_GAP
      })

      return {
        contentHeight: nextTop + GRID_PADDING - GROUP_GAP,
        rows: nextRows
      }
    }, [cellSize, groups, rowPitch, visible])

    const visibleRows = useMemo(() => {
      if (!visible || rows.length === 0) return []

      const measuredViewportHeight = viewportHeight || DEFAULT_VIEWPORT_HEIGHT
      const overscanDistance = rowPitch * OVERSCAN_ROWS
      const visibleStart = Math.max(0, scrollTop - overscanDistance)
      const visibleEnd = scrollTop + measuredViewportHeight + overscanDistance
      const firstIndex = findFirstVisibleRowIndex(rows, visibleStart)
      const nextVisibleRows = []

      for (let index = firstIndex; index < rows.length; index++) {
        const row = rows[index]
        if (row.top > visibleEnd) break
        nextVisibleRows.push(row)
      }

      return nextVisibleRows
    }, [rowPitch, rows, scrollTop, viewportHeight, visible])

    return (
      <Modal
        visible={visible}
        title="Select References"
        onCancel={handleCancel}
        width={860}
        centered
        destroyOnClose
        maskClosable={false}
        style={{ maxWidth: 'calc(100vw - 32px)' }}
        bodyStyle={{
          padding: 0,
          height: '68vh',
          display: 'flex',
          flexDirection: 'column'
        }}
        footer={[
          <Button key="clear" onClick={handleClear}>
            Clear
          </Button>,
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={handleApply}
            style={{ background: COLORS.primary, borderColor: COLORS.primary }}
          >
            Use Selected
          </Button>
        ]}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${COLORS.borderLight}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <ReferenceSearch value={searchTerm} onChange={setSearchTerm} />
          </div>
          <Badge
            count={draftSelectedReferences.length}
            showZero
            style={{ backgroundColor: COLORS.primary }}
          />
          <span
            style={{
              fontSize: 12,
              color: COLORS.textSecondary,
              whiteSpace: 'nowrap'
            }}
          >
            selected
          </span>
        </div>

        <div
          ref={viewportRef}
          onScroll={handleScroll}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            background: COLORS.white
          }}
        >
          {totalCount === 0 ? (
            <div
              style={{
                padding: 32,
                textAlign: 'center',
                color: COLORS.textMuted,
                fontSize: 13
              }}
            >
              {debouncedSearchTerm ? 'No matches' : 'No references'}
            </div>
          ) : (
            <div style={{ position: 'relative', height: contentHeight }}>
              {visibleRows.map(row =>
                row.type === 'header' ? (
                  <div
                    key={row.key}
                    style={{
                      position: 'absolute',
                      top: row.top,
                      left: GRID_PADDING,
                      right: GRID_PADDING,
                      height: row.height,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '0 4px',
                      fontSize: 11,
                      fontWeight: 500,
                      color: COLORS.textSecondary
                    }}
                  >
                    <Icon type={getGroupIcon(row.group.key)} />
                    <span>{row.group.title}</span>
                    <span style={{ color: COLORS.textMuted }}>
                      ({row.group.images.length})
                    </span>
                  </div>
                ) : (
                  <div
                    key={row.key}
                    style={{
                      position: 'absolute',
                      top: row.top,
                      left: GRID_PADDING,
                      right: GRID_PADDING,
                      height: row.height,
                      display: 'grid',
                      gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
                      gap: GRID_GAP
                    }}
                  >
                    {row.images.map(image => (
                      <ReferenceImage
                        key={image.url}
                        image={image}
                        selected={selectedSet.has(image.url)}
                        deferImage={isScrolling}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </Modal>
    )
  }
)

export default ReferencePanel
